/**
 * API Service Module
 * Centralized API calls using Axios with base URL and interceptors
 * Includes functions for auth, projects, RFPs, bids, payments, contracts, and more
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,   // send httpOnly JWT cookie on every request
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach token from sessionStorage if present
// This is the iOS Safari fallback (httpOnly cookie is blocked cross-origin by ITP)
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response error handler
// Note: we do NOT hard-redirect to /login here.
// AuthContext's 401 interceptor calls setUser(null), and React Router's ProtectedRoute
// handles the redirect. A hard window.location redirect caused an iPhone-only flash
// (login success → page shows briefly → hard reload back to /login).
const handleError = (error) => {
  if (error.response?.status === 401) {
    localStorage.removeItem('user');
    // Let AuthContext's interceptor handle setUser(null) and routing
  }
  throw error;
};

// ============= AUTH ENDPOINTS =============

const authAPI = {
  login: (email, password) =>
    api.post('/auth/login', { email, password }).catch(handleError),

  register: (data) =>
    api.post('/auth/register', data).catch(handleError),

  logout: () =>
    api.post('/auth/logout').catch(handleError),

  refreshToken: (refreshToken) =>
    api.post('/auth/refresh-token', { refreshToken }).catch(handleError),

  googleAuth: (googleToken) =>
    api.post('/auth/google', { googleToken }).catch(handleError),

  forgotPassword: (email) =>
    api.post('/auth/forgot-password', { email }).catch(handleError),

  resetPassword: (token, newPassword) =>
    api.put(`/auth/reset-password/${token}`, { newPassword }).catch(handleError),

  verifyEmail: (token) =>
    api.post('/auth/verify-email', { token }).catch(handleError),

  resendVerification: (email) =>
    api.post('/auth/resend-verification', { email }).catch(handleError),

  updateProfile: (data) =>
    api.put('/auth/update-profile', data).catch(handleError),

  updatePassword: (currentPassword, newPassword) =>
    api.put('/auth/update-password', { currentPassword, newPassword }).catch(handleError),

  changeEmail: (newEmail, currentPassword) =>
    api.post('/auth/change-email', { newEmail, currentPassword }).catch(handleError),

  confirmEmailChange: (token) =>
    api.get(`/auth/confirm-email-change/${token}`).catch(handleError),

  requestDeletion: () =>
    api.post('/auth/request-deletion').catch(handleError),

  cancelDeletionRequest: () =>
    api.delete('/auth/request-deletion').catch(handleError),
};

// ============= PROJECTS ENDPOINTS =============

const projectsAPI = {
  create: (projectData) =>
    api.post('/projects', projectData).catch(handleError),

  getAll: (params = {}) =>
    api.get('/projects', { params }).catch(handleError),

  getById: (projectId) =>
    api.get(`/projects/${projectId}`).catch(handleError),

  update: (projectId, data) =>
    api.put(`/projects/${projectId}`, data).catch(handleError),

  delete: (projectId) =>
    api.delete(`/projects/${projectId}`).catch(handleError),

  uploadImages: (projectId, formData) =>
    api.post(`/projects/${projectId}/upload-images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).catch(handleError),

  // Submit answers to sufficiency follow-up questions → triggers GPT-4 bid request generation
  submitFollowUpAnswers: (projectId, answers) =>
    api.post(`/projects/${projectId}/clarify`, { answers }).catch(handleError),
};

// ============= RFP ENDPOINTS =============

const rfpsAPI = {
  getAll: (params = {}) =>
    api.get('/rfps', { params }).catch(handleError),

  getById: (rfpId) =>
    api.get(`/rfps/${rfpId}`).catch(handleError),

  getByProject: (projectId) =>
    api.get(`/rfps/project/${projectId}`).catch(handleError),

  search: (query) =>
    api.get('/rfps/search', { params: { q: query } }).catch(handleError),

  filter: (filters) =>
    api.get('/rfps/filter', { params: filters }).catch(handleError),

  getStats: (rfpId) =>
    api.get(`/rfps/${rfpId}/stats`).catch(handleError),

  close: (rfpId) =>
    api.put(`/rfps/${rfpId}/close`).catch(handleError),

  repost: (rfpId) =>
    api.put(`/rfps/${rfpId}/repost`).catch(handleError),
};

// ============= BIDS ENDPOINTS =============

const bidsAPI = {
  submit: (rfpId, bidData) =>
    api.post('/bids', { rfpId, ...bidData }).catch(handleError),

  getByRFP: (rfpId) =>
    api.get(`/rfps/${rfpId}/bids`).catch(handleError),

  getByContractor: (params = {}) =>
    api.get('/bids/my-bids', { params }).catch(handleError),

  getById: (bidId) =>
    api.get(`/bids/${bidId}`).catch(handleError),

  update: (bidId, data) =>
    api.put(`/bids/${bidId}`, data).catch(handleError),

  withdraw: (bidId) =>
    api.delete(`/bids/${bidId}`).catch(handleError),

  accept: (bidId) =>
    api.put(`/bids/${bidId}/accept`).catch(handleError),
};

// ============= PAYMENTS ENDPOINTS =============

const paymentsAPI = {
  createPaymentIntent: (bidId, amount) =>
    api.post('/payments/create-intent', { bidId, amount }).catch(handleError),

  confirmPayment: (paymentIntentId, paymentMethodId) =>
    api.post('/payments/confirm', {
      paymentIntentId,
      paymentMethodId,
    }).catch(handleError),

  releaseEscrow: (projectId) =>
    api.post(`/payments/release-escrow/${projectId}`).catch(handleError),

  getTransactions: (params = {}) =>
    api.get('/payments/transactions', { params }).catch(handleError),

  refund: (paymentId, reason) =>
    api.post(`/payments/${paymentId}/refund`, { reason }).catch(handleError),

  // Legacy escrow flow (bid → pay → sign) — kept for backward compat
  createEscrowIntent: (bidId) =>
    api.post('/payments/escrow/intent', { bidId }).catch(handleError),

  confirmEscrowPayment: (paymentIntentId) =>
    api.post('/payments/escrow/confirm', { paymentIntentId }).catch(handleError),

  requestEscrowRelease: (paymentId) =>
    api.post(`/payments/escrow/${paymentId}/release`).catch(handleError),

  getEscrowByBid: (bidId) =>
    api.get(`/payments/escrow/bid/${bidId}`).catch(handleError),

  // Contract → Sign → Pay flow (new)
  fundContractEscrowIntent: (contractId) =>
    api.post(`/payments/contract/${contractId}/escrow-intent`).catch(handleError),

  confirmContractEscrowPayment: (contractId, paymentIntentId) =>
    api.post(`/payments/contract/${contractId}/escrow-confirm`, { paymentIntentId }).catch(handleError),

  getFeePreview: (amount) =>
    api.get('/payments/fee-preview', { params: { amount } }).catch(handleError),

  // BNPL financing flow
  createFinancingIntent: (bidId) =>
    api.post('/payments/financing/intent', { bidId }).catch(handleError),

  confirmFinancingPayment: (paymentIntentId) =>
    api.post('/payments/financing/confirm', { paymentIntentId }).catch(handleError),

  // Stripe Connect (contractor payout account)
  startConnect: () =>
    api.post('/payments/connect/start').catch(handleError),

  getConnectStatus: () =>
    api.get('/payments/connect/status').catch(handleError),

  relinkConnect: () =>
    api.get('/payments/connect/relink').catch(handleError),

  // Dispute
  createDispute: (paymentId, reason) =>
    api.post('/payments/dispute', { paymentId, reason }).catch(handleError),

  // Homeowner refund request
  requestRefund: (contractId) =>
    api.post('/payments/request-refund', { contractId }).catch(handleError),

  checkRefundEligibility: (contractId) =>
    api.get(`/payments/refund-eligibility/${contractId}`).catch(handleError),

  // Card on file (contractors)
  getCardSetupIntent: () =>
    api.post('/payments/card/setup-intent').catch(handleError),

  saveCard: (paymentMethodId) =>
    api.post('/payments/card/save', { paymentMethodId }).catch(handleError),

  getCardStatus: () =>
    api.get('/payments/card/status').catch(handleError),

  removeCard: () =>
    api.delete('/payments/card').catch(handleError),

  // Project slot purchase — $5.00 unlocks 5 additional submission slots
  createSlotPurchaseIntent: () =>
    api.post('/payments/slots/intent').catch(handleError),

  confirmSlotPurchase: (paymentIntentId) =>
    api.post('/payments/slots/confirm', { paymentIntentId }).catch(handleError),
};

// ============= SOCIAL SHARES ENDPOINTS =============
const socialSharesAPI = {
  generate: (platform) =>
    api.post('/social-shares/generate', { platform }).catch(handleError),

  getStatus: () =>
    api.get('/social-shares/status').catch(handleError),

  adminUserStats: (params) =>
    api.get('/social-shares/admin/user-stats', { params }).catch(handleError),
};

const referralAPI = {
  getBalance: () =>
    api.get('/referrals/balance').catch(handleError),

  requestWithdrawal: (data) =>
    api.post('/referrals/withdraw', data).catch(handleError),

  getReferredUsers: () =>
    api.get('/referrals/referred-users').catch(handleError),

  applyToSlots: () =>
    api.post('/referrals/apply-to-slots').catch(handleError),

  saveTaxInfo: (data) =>
    api.post('/referrals/tax-info', data).catch(handleError),

  adminGetWithdrawals: (params) =>
    api.get('/referrals/admin/withdrawals', { params }).catch(handleError),

  adminProcessWithdrawal: (id, data) =>
    api.put(`/referrals/admin/withdrawals/${id}/process`, data).catch(handleError),
};

// ============= CONTRACTS ENDPOINTS =============

const contractsAPI = {
  // Contract → Sign → Pay flow: accept a bid and generate the contract (no payment yet)
  acceptBid: (bidId) =>
    api.post('/contracts/generate', { bidId }).catch(handleError),

  // Legacy alias (same endpoint)
  generate: (bidId) =>
    api.post('/contracts/generate', { bidId }).catch(handleError),

  sign: (contractId, signature) =>
    api.post(`/contracts/${contractId}/sign`, { signature }).catch(handleError),

  getById: (contractId) =>
    api.get(`/contracts/${contractId}`).catch(handleError),

  // Alias used by LeaveReview page
  getOne: (contractId) =>
    api.get(`/contracts/${contractId}`).catch(handleError),

  getByProject: (projectId) =>
    api.get(`/contracts/project/${projectId}`).catch(handleError),

  download: (contractId) =>
    api.get(`/contracts/${contractId}/download`, {
      responseType: 'blob',
    }).catch(handleError),

  // Contractor post-signature cancellation (charges 5% exit fee, capped $250)
  cancelByContractor: (contractId) =>
    api.put(`/contracts/${contractId}/cancel`).catch(handleError),

  // Homeowner cancellation — free until homeowner confirms ready to proceed, fee applies after
  cancelByHomeowner: (contractId) =>
    api.put(`/contracts/${contractId}/cancel-homeowner`).catch(handleError),

  // Homeowner confirms ready to proceed — reveals full contact details and closes free-cancel window
  confirmProceed: (contractId) =>
    api.put(`/contracts/${contractId}/confirm-proceed`).catch(handleError),

  // Contractor dismisses a cancelled contract from their dashboard
  dismiss: (contractId) =>
    api.put(`/contracts/${contractId}/dismiss`).catch(handleError),

  // Get all contracts for the current user
  getAll: (params = {}) =>
    api.get('/contracts/user/all', { params }).catch(handleError),

  // ── Milestone Payment API ──────────────────────────────────────────────
  // Update AI-generated milestone schedule (homeowner, pre-signing only)
  updateMilestones: (contractId, milestones) =>
    api.put(`/contracts/${contractId}/milestones`, { milestones }).catch(handleError),

  // Homeowner pays next milestone tranche (off-session Stripe charge)
  payMilestone: (contractId, milestoneIndex) =>
    api.post(`/contracts/${contractId}/milestones/${milestoneIndex}/pay`).catch(handleError),

  // Contractor requests payment release — optionally with a different amount
  markMilestoneReady: (contractId, milestoneIndex, body = {}) =>
    api.put(`/contracts/${contractId}/milestones/${milestoneIndex}/ready`, body).catch(handleError),

  // Homeowner confirms milestone and releases funds to contractor
  confirmMilestone: (contractId, milestoneIndex) =>
    api.put(`/contracts/${contractId}/milestones/${milestoneIndex}/confirm`).catch(handleError),
};

// ============= REVIEWS ENDPOINTS =============

const reviewsAPI = {
  // POST /api/reviews — body: { contractorId, projectId, rating, categories?, comment? }
  submit: (reviewData) =>
    api.post('/reviews', reviewData).catch(handleError),

  getByContractor: (contractorId) =>
    api.get(`/reviews/contractor/${contractorId}`).catch(handleError),

  getByProject: (projectId) =>
    api.get(`/reviews/project/${projectId}`).catch(handleError),

  update: (reviewId, data) =>
    api.put(`/reviews/${reviewId}`, data).catch(handleError),

  delete: (reviewId) =>
    api.delete(`/reviews/${reviewId}`).catch(handleError),
};

// ============= MESSAGES ENDPOINTS =============

const messagesAPI = {
  // POST /api/messages/conversations/:id/messages
  send: (conversationId, messageData) =>
    api.post(`/messages/conversations/${conversationId}/messages`, messageData).catch(handleError),

  // GET /api/messages/conversations/:id
  getConversation: (conversationId, params = {}) =>
    api.get(`/messages/conversations/${conversationId}`, { params }).catch(handleError),

  // GET /api/messages/conversations
  getConversations: (params = {}) =>
    api.get('/messages/conversations', { params }).catch(handleError),

  // POST /api/messages/conversations — find or create between two users on a project
  startConversation: (participantId, projectId) =>
    api.post('/messages/conversations', { participantId, projectId }).catch(handleError),

  // Admin endpoints
  adminGetConversations: (params = {}) =>
    api.get('/messages/admin/conversations', { params }).catch(handleError),

  adminGetConversation: (conversationId) =>
    api.get(`/messages/admin/conversations/${conversationId}`).catch(handleError),
};

// ============= CONTRACTORS ENDPOINTS =============

const contractorsAPI = {
  getProfile: (contractorId) =>
    api.get(`/contractors/${contractorId}`).catch(handleError),

  getAll: (params = {}) =>
    api.get('/contractors', { params }).catch(handleError),

  search: (query, filters = {}) =>
    api.get('/contractors/search', {
      params: { q: query, ...filters },
    }).catch(handleError),

  updateProfile: (data) =>
    api.put('/contractors/profile', data).catch(handleError),

  uploadPortfolio: (formData) =>
    api.post('/contractors/portfolio', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).catch(handleError),

  getStats: (contractorId) =>
    api.get(`/contractors/${contractorId}/stats`).catch(handleError),
};

// ============= ADMIN ENDPOINTS =============

const adminAPI = {
  getUsers: (params = {}) =>
    api.get('/admin/users', { params }).catch(handleError),

  updateUser: (userId, data) =>
    api.put(`/admin/users/${userId}`, data).catch(handleError),

  getTransactions: (params = {}) =>
    api.get('/admin/transactions', { params }).catch(handleError),

  approveContractor: (contractorId) =>
    api.post(`/admin/contractors/${contractorId}/approve`).catch(handleError),

  rejectContractor: (contractorId, reason) =>
    api.post(`/admin/contractors/${contractorId}/reject`, {
      reason,
    }).catch(handleError),

  generateReport: (type, params = {}) =>
    api.get(`/admin/reports/${type}`, { params }).catch(handleError),

  getDashboard: () =>
    api.get('/admin/dashboard').catch(handleError),

  updateFeeSettings: (settings) =>
    api.put('/admin/fee-settings', settings).catch(handleError),

  getDisputes: (params = {}) =>
    api.get('/admin/disputes', { params }).catch(handleError),

  getDisputeDetail: (disputeId) =>
    api.get(`/admin/disputes/${disputeId}`).catch(handleError),

  sendDisputeMessage: (disputeId, data) =>
    api.post(`/admin/disputes/${disputeId}/message`, data).catch(handleError),

  resolveDispute: (disputeId, data) =>
    api.post(`/admin/disputes/${disputeId}/resolve`, data).catch(handleError),

  getContracts: (params = {}) =>
    api.get('/admin/contracts', { params }).catch(handleError),

  getPenaltyFees: (params = {}) =>
    api.get('/admin/penalty-fees', { params }).catch(handleError),

  getPendingVerifications: () =>
    api.get('/admin/contractors/pending-verification').catch(handleError),

  approveVerification: (contractorId, serviceType) =>
    api.put(`/admin/contractors/${contractorId}/verify`, { serviceType, action: 'approve' }).catch(handleError),

  rejectVerification: (contractorId, serviceType, reason) =>
    api.put(`/admin/contractors/${contractorId}/verify`, { serviceType, action: 'reject', reason }).catch(handleError),

  waivePenaltyFee: (userId, feeId) =>
    api.put(`/admin/penalty-fees/${userId}/${feeId}/waive`).catch(handleError),

  forceRefund: (paymentId, amountDollars) =>
    api.post(`/admin/payments/${paymentId}/force-refund`, amountDollars != null ? { amountDollars } : {}).catch(handleError),

  forceRelease: (paymentId, amountDollars) =>
    api.post(`/admin/payments/${paymentId}/force-release`, amountDollars != null ? { amountDollars } : {}).catch(handleError),

  deleteUser: (userId) =>
    api.delete(`/admin/users/${userId}`).catch(handleError),

  verifyUserEmail: (userId) =>
    api.post(`/admin/users/${userId}/verify-email`).catch(handleError),

  resendUserVerification: (userId) =>
    api.post(`/admin/users/${userId}/resend-verification`).catch(handleError),

  getProjects: (params = {}) =>
    api.get('/admin/projects', { params }).catch(handleError),

  deleteProject: (projectId) =>
    api.delete(`/admin/projects/${projectId}`).catch(handleError),

  // Ban user (add to blocklist + optionally delete account)
  banUser: (userId, reason, deleteAccount = true) =>
    api.post(`/admin/users/${userId}/ban`, { reason, deleteAccount }).catch(handleError),

  // Banned email list
  getBannedEmails: (params = {}) =>
    api.get('/admin/banned-emails', { params }).catch(handleError),

  // Unban (remove from blocklist)
  unbanEmail: (banId) =>
    api.delete(`/admin/banned-emails/${banId}`).catch(handleError),

  // ── Milestone Override ────────────────────────────────────────────────────
  // Get all milestone-payment contracts
  getMilestoneContracts: (params = {}) =>
    api.get('/admin/milestones', { params }).catch(handleError),

  // Force-release a specific milestone phase (bypasses homeowner approval)
  forceReleaseMilestone: (contractId, milestoneId, note = '') =>
    api.post(`/admin/contracts/${contractId}/milestones/${milestoneId}/force-release`, { note }).catch(handleError),

  // Override mandatory milestone rule on a contract
  overrideMandatoryMilestone: (contractId, disable, note = '') =>
    api.post(`/admin/contracts/${contractId}/milestones/override-mandatory`, { disable, note }).catch(handleError),

  // Approve a supplemental materials release
  approveSupplementalRelease: (contractId, releaseId, note = '') =>
    api.post(`/admin/contracts/${contractId}/supplemental-release/${releaseId}/approve`, { note }).catch(handleError),

  // Platform settings
  getSettings: () =>
    api.get('/admin/settings').catch(handleError),

  updateSettings: (data) =>
    api.put('/admin/settings', data).catch(handleError),

  waiveMembership: (userId) =>
    api.put(`/admin/membership/${userId}/waive`).catch(handleError),

  // Account deletion requests
  getDeletionRequests: () =>
    api.get('/admin/deletion-requests').catch(handleError),

  // Admin re-screen: trigger AI pre-screen for a contractor's licensed service
  rescreenService: (userId, serviceId) =>
    api.post(`/admin/contractors/${userId}/services/${serviceId}/rescreen`).catch(handleError),

  // Cancel a contract (admin-initiated, no charge)
  cancelContract: (contractId) =>
    api.put(`/admin/contracts/${contractId}/cancel`).catch(handleError),
};

// ============= NOTIFICATIONS ENDPOINTS =============

const notificationsAPI = {
  getAll: (params = {}) =>
    api.get('/notifications', { params }).catch(handleError),

  markRead: (notificationId) =>
    api.put(`/notifications/${notificationId}/read`).catch(handleError),

  markAllRead: () =>
    api.put('/notifications/mark-all-read').catch(handleError),

  delete: (notificationId) =>
    api.delete(`/notifications/${notificationId}`).catch(handleError),

  getPreferences: () =>
    api.get('/notifications/preferences').catch(handleError),

  updatePreferences: (preferences) =>
    api.put('/notifications/preferences', preferences).catch(handleError),
};

// ============= PUBLIC STATS =============

const statsAPI = {
  get: () => api.get('/stats').catch(handleError),
};

// ============= ESTIMATES ENDPOINTS =============

const estimatesAPI = {
  // Contractor: submit a price-range estimate for an estimate-type project
  submit: (projectId, data) =>
    api.post(`/estimates/${projectId}`, data).catch(handleError),

  // Homeowner: get all estimate responses for their project
  getForProject: (projectId) =>
    api.get(`/estimates/${projectId}`).catch(handleError),

  // Contractor: get open estimate projects (projectType=estimate)
  getOpenRequests: (params = {}) =>
    api.get('/projects', { params: { projectType: 'estimate', ...params } }).catch(handleError),

  // Contractor: check monthly usage against their tier limit
  getUsage: () =>
    api.get('/estimates/usage').catch(handleError),
};

// ============= EXPORT ALL MODULES =============

const disputesAPI = {
  openDispute: (formData) =>
    api.post('/disputes', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).catch(handleError),

  submitResponse: (disputeId, formData) =>
    api.put(`/disputes/${disputeId}/respond`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).catch(handleError),

  getDispute: (disputeId) =>
    api.get(`/disputes/${disputeId}`).catch(handleError),

  getUserDisputes: () =>
    api.get('/disputes/user/all').catch(handleError),
};

export default {
  auth: authAPI,
  projects: projectsAPI,
  rfps: rfpsAPI,
  bids: bidsAPI,
  payments: paymentsAPI,
  contracts: contractsAPI,
  reviews: reviewsAPI,
  messages: messagesAPI,
  contractors: contractorsAPI,
  admin: adminAPI,
  disputes: disputesAPI,
  notifications: notificationsAPI,
  stats: statsAPI,
  estimates: estimatesAPI,
  contact: {
    sendMessage: (data) => api.post('/contact', data).catch(handleError),
  },
  support: {
    sendMessage: (data) => api.post('/support/contact', data).catch(handleError),
  },
  membership: {
    getPublicSettings: () => api.get('/membership/public-settings').catch(handleError),
    getStatus:         () => api.get('/membership/status').catch(handleError),
    createCheckout:    () => api.post('/membership/checkout').catch(handleError),
  },
  subscriptions: {
    getPlans: () => api.get('/subscriptions/plans').catch(handleError),
    getStatus: () => api.get('/subscriptions/status').catch(handleError),
    createCheckout: (tier) => api.post('/subscriptions/checkout', { tier }).catch(handleError),
    cancel: () => api.post('/subscriptions/cancel').catch(handleError),
  },

  homeownerSubscription: {
    getPlans:       () => api.get('/homeowner-subscription/plans').catch(handleError),
    getStatus:      () => api.get('/homeowner-subscription/status').catch(handleError),
    createCheckout: () => api.post('/homeowner-subscription/checkout').catch(handleError),
    cancel:         () => api.post('/homeowner-subscription/cancel').catch(handleError),
  },

  milestones: {
    // Get contractor's reputation tier + max first-milestone %
    getContractorTier: () =>
      api.get('/milestones/tier').catch(handleError),

    // Get all milestones for a contract
    getForContract: (contractId) =>
      api.get(`/milestones/contract/${contractId}`).catch(handleError),

    // Contractor marks a milestone complete
    markComplete: (contractId, milestoneId, completionNote = '') =>
      api.post(`/milestones/${contractId}/${milestoneId}/complete`, { completionNote }).catch(handleError),

    // Homeowner releases payment for a milestone
    release: (contractId, milestoneId) =>
      api.post(`/milestones/${contractId}/${milestoneId}/release`).catch(handleError),

    // Either party disputes a milestone
    dispute: (contractId, milestoneId, reason) =>
      api.post(`/milestones/${contractId}/${milestoneId}/dispute`, { reason }).catch(handleError),

    // Homeowner reports project abandonment
    reportAbandonment: (contractId) =>
      api.post(`/milestones/${contractId}/abandon`).catch(handleError),

    // AI-generated milestone schedule for contractor bid form
    suggest: ({ serviceType, totalAmount, scopeOfWork = '' }) =>
      api.post('/milestones/suggest', { serviceType, totalAmount, scopeOfWork }).catch(handleError),
  },

  // ── MeiliSearch-powered search ─────────────────────────────────────────────
  search: {
    /**
     * Search approved contractors
     * @param {object} params — q, serviceType, state, lat, lng, radius, sort, page, limit
     */
    contractors: (params = {}) =>
      api.get('/search/contractors', { params }).catch(handleError),

    /**
     * Search open RFPs
     * @param {object} params — q, status, serviceType, state, tags, lat, lng, radius, sort, page, limit
     */
    rfps: (params = {}) =>
      api.get('/search/rfps', { params }).catch(handleError),

    /**
     * Admin: bulk-sync all contractors + RFPs into MeiliSearch
     */
    sync: () =>
      api.post('/search/sync').catch(handleError),
  },

  // ── Change Orders ──────────────────────────────────────────────────────────
  changeOrders: {
    /**
     * Contractor submits a change order (multipart/form-data with optional evidence files)
     * @param {FormData} formData — contractId, category, requestedAdditionalAmount, description, evidence[]
     */
    create: (formData) =>
      api.post('/change-orders', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).catch(handleError),

    /** Get all change orders for a given contract */
    getForContract: (contractId) =>
      api.get(`/change-orders/contract/${contractId}`).catch(handleError),

    /**
     * Homeowner approves or rejects a change order
     * @param {object} body — { decision: 'approved'|'rejected', reason?: string }
     */
    respond: (id, body) =>
      api.put(`/change-orders/${id}/respond`, body).catch(handleError),

    /**
     * Contractor submits a counter-offer after round 1 rejection
     * @param {object} body — { amount: number, description: string }
     */
    counterOffer: (id, body) =>
      api.put(`/change-orders/${id}/counter`, body).catch(handleError),

    /** Either party escalates a final-rejected hidden_condition order to admin */
    escalate: (id) =>
      api.put(`/change-orders/${id}/escalate`).catch(handleError),

    /** Admin: list change orders by status (default: admin_review) */
    adminQueue: (status = 'admin_review') =>
      api.get('/change-orders/admin/queue', { params: { status } }).catch(handleError),

    /** Admin: issue a binding ruling on a change order */
    adminRule: (id, body) =>
      api.put(`/change-orders/admin/${id}/rule`, body).catch(handleError),
  },

  socialShares: socialSharesAPI,
  referral: referralAPI,
};
